import type { AuditRunReport, RunReportCheckpoint } from '../../../services/amsAuditRunReportService';

/**
 * Report generation for a submitted (locked) AMS audit.
 *
 * Both reports are derived from the run-report payload the office already
 * fetches — the submitted audit IS the source. No extra endpoint, no PDF
 * dependency: the documents are printable HTML handed to the browser's own
 * print/"Save as PDF" (ponytail: native print beats a PDF lib until someone
 * needs server-side archival copies).
 */

export interface ReportMeta {
  projectName?: string;
  auditLabel?: string;
  /** ISO string; the caller stamps it so the builders stay pure/testable. */
  generatedAt?: string;
}

/** A single non-conformance: a failed/held checkpoint or a failed module finding. */
export interface NcrItem {
  ref: string;
  source: 'checkpoint' | 'finding';
  equipment: string;
  title: string;
  status: string;
  severity?: string;
  impact?: string;
  observation?: string;
  action?: string;
  reading?: string;
  evidence: string[];
}

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const equipLabel = (equip: string) => equip || 'Project-level';

/** Non-conformances: checkpoints that failed or are on hold, plus failed findings. */
export function collectNcrItems(report: AuditRunReport): NcrItem[] {
  const items: NcrItem[] = [];
  for (const cp of report.checkpoints) {
    if (cp.status !== 'fail' && cp.status !== 'hold') continue;
    items.push({
      ref: `NCR-${items.length + 1}`,
      source: 'checkpoint',
      equipment: equipLabel(cp.equip),
      title: cp.title,
      status: cp.status,
      severity: cp.severity,
      impact: cp.impact,
      observation: cp.observation,
      action: cp.action,
      reading: cp.reading ? `${cp.reading}${cp.unit ? ` ${cp.unit}` : ''}` : undefined,
      evidence: cp.attachments ?? [],
    });
  }
  for (const f of report.findings) {
    if (f.pass) continue;
    items.push({
      ref: `NCR-${items.length + 1}`,
      source: 'finding',
      equipment: equipLabel(f.equip),
      title: f.modules.length > 0 ? `Module finding — ${f.modules.join(', ')}` : 'Module finding',
      status: 'fail',
      severity: f.severity,
      impact: f.impact,
      observation: f.observation,
      action: f.action,
      evidence: f.photos,
    });
  }
  return items;
}

/** Checkpoint rows grouped by equipment, project-level last. */
function groupByEquipment(rows: RunReportCheckpoint[]): Array<{ heading: string; rows: RunReportCheckpoint[] }> {
  const map = new Map<string, RunReportCheckpoint[]>();
  for (const row of rows) {
    const key = row.equip || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : 0))
    .map(([key, group]) => ({ heading: equipLabel(key), rows: group }));
}

const STYLE = `
  body{font:12px/1.45 system-ui,-apple-system,Segoe UI,sans-serif;color:#111;margin:24px}
  h1{font-size:18px;margin:0 0 2px}
  h2{font-size:13px;margin:20px 0 6px;padding-bottom:3px;border-bottom:1px solid #ddd}
  .sub{color:#555;margin:0 0 14px}
  table{width:100%;border-collapse:collapse;margin-bottom:10px}
  th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top}
  th{background:#f3f4f6;font-weight:600}
  .totals td{font-weight:600}
  .pass{color:#047857}.fail{color:#b91c1c}.hold{color:#b45309}.due{color:#6b7280}
  .empty{color:#6b7280;font-style:italic}
  dl{margin:0}dt{font-weight:600;float:left;width:110px;clear:left}dd{margin:0 0 2px 118px}
  @media print{body{margin:0}h2{break-after:avoid}tr{break-inside:avoid}}
`;

function shell(title: string, meta: ReportMeta, body: string): string {
  const stamp = meta.generatedAt ? new Date(meta.generatedAt).toLocaleString() : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}</style></head><body>
<h1>${esc(title)}</h1>
<p class="sub">${esc(meta.projectName ?? '')}${meta.projectName && meta.auditLabel ? ' — ' : ''}${esc(meta.auditLabel ?? '')}${stamp ? ` · generated ${esc(stamp)}` : ''}</p>
${body}
</body></html>`;
}

/** Checkpoint Summary Report: every checkpoint of the submitted audit with its result. */
export function buildCheckpointSummaryReport(report: AuditRunReport, meta: ReportMeta = {}): string {
  const p = report.progress;
  const totals = `<table><tr><th>Checkpoints</th><th>Completed</th><th>Pass</th><th>Fail</th><th>On hold</th><th>Not done</th></tr>
<tr class="totals"><td>${p.total}</td><td>${p.done}</td><td class="pass">${p.pass}</td><td class="fail">${p.fail}</td><td class="hold">${p.hold}</td><td class="due">${p.due}</td></tr></table>`;

  const signoff = `<table><tr><th>Run status</th><td>${esc(report.audit.runStatus)}</td><th>Approval</th><td>${esc(report.audit.approvalStatus)}</td></tr>
<tr><th>OMS/vendor check-in</th><td>${report.audit.omsChecked ? esc(report.audit.omsRep || 'yes') : 'not recorded'}</td><th>Signed off by</th><td>${esc(report.audit.signedOff.join(', ') || '—')}</td></tr></table>`;

  const sections = groupByEquipment(report.checkpoints)
    .map(
      (g) => `<h2>${esc(g.heading)}</h2>
<table><tr><th>Checkpoint</th><th>Type</th><th>Expected</th><th>Reading</th><th>Result</th></tr>
${g.rows
  .map(
    (r) => `<tr><td>${esc(r.title)}</td><td>${esc(r.type)}</td><td>${esc(r.ideal ?? '—')}${r.ideal && r.unit ? ` ${esc(r.unit)}` : ''}</td><td>${esc(r.reading ?? '—')}${r.reading && r.unit ? ` ${esc(r.unit)}` : ''}</td><td class="${esc(r.status)}">${esc(r.status)}</td></tr>`,
  )
  .join('')}
</table>`,
    )
    .join('');

  return shell(
    'Checkpoint Summary Report',
    meta,
    `${totals}${signoff}${sections || '<p class="empty">No checkpoints recorded for this audit.</p>'}`,
  );
}

/** Non-Conformance Report: only the failures and holds, each as a numbered NCR. */
export function buildNcrReport(report: AuditRunReport, meta: ReportMeta = {}): string {
  const items = collectNcrItems(report);
  if (items.length === 0) {
    return shell('Non-Conformance Report', meta, '<p class="empty">No non-conformances recorded — all checkpoints passed.</p>');
  }
  const rows = items
    .map(
      (i) => `<h2>${esc(i.ref)} — ${esc(i.equipment)}</h2>
<dl>
<dt>Checkpoint</dt><dd>${esc(i.title)}</dd>
<dt>Result</dt><dd class="${esc(i.status)}">${esc(i.status)}${i.reading ? ` (reading: ${esc(i.reading)})` : ''}</dd>
<dt>Severity</dt><dd>${esc(i.severity ?? '—')}</dd>
<dt>Impact</dt><dd>${esc(i.impact ?? '—')}</dd>
<dt>Observation</dt><dd>${esc(i.observation ?? '—')}</dd>
<dt>Corrective action</dt><dd>${esc(i.action ?? '—')}</dd>
<dt>Evidence</dt><dd>${i.evidence.length === 0 ? '—' : i.evidence.map((u, n) => `<a href="${esc(u)}">photo ${n + 1}</a>`).join(' · ')}</dd>
</dl>`,
    )
    .join('');
  return shell('Non-Conformance Report', meta, `<p class="sub">${items.length} non-conformance(s) raised.</p>${rows}`);
}

/** Hand a built report to the browser's own print / Save-as-PDF dialog. */
export function printReport(html: string): void {
  const win = window.open('', '_blank');
  if (!win) return; // popup blocked — caller surfaces nothing; user retries
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}
